import { useNavigate } from '@tanstack/react-router'
import { Layers, Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { usePools } from '@/api/endpoints/pools'
import { useSchedulableFactories } from '@/api/endpoints/scheduler'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { Button } from '@/components/ui/Button'
import { CreatePoolDialog } from '@/features/pools/components/CreatePoolDialog'
import { PoolCarousel } from '@/features/pools/components/PoolCarousel'
import { PoolDetail } from '@/features/pools/components/PoolDetail'
import { keepChannel, poolToReopen, readLastPool, rememberPool } from '@/features/pools/lastPool'
import { buildPoolCards, visiblePools } from '@/features/scheduler/poolCards'
import styles from './PoolsPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

interface PoolsPageProps {
  /** O pool aberto (``/pools/$slug``); ``null`` na lista. */
  selectedSlug: string | null
  /** ``/pools?new=true``: abre a criação (o "Novo pool" do Scheduler). */
  openCreate: boolean
}

/**
 * Os pools do dono: um carrossel em cima e o pool aberto embaixo. Um pool é um
 * acervo de vídeos sem marca que gera com agenda própria; os canais ligados
 * publicam dele. Ver docs/plan-video-pools.md.
 */
export function PoolsPage({ selectedSlug, openCreate }: PoolsPageProps) {
  const navigate = useNavigate()
  const { data: pools, isPending, isError, error, refetch } = usePools()
  const { data: factories } = useSchedulableFactories()
  const [createOpen, setCreateOpen] = useState(openCreate)
  // Para o "hoje 14:00" dos avisos e o "nas últimas 24 h": relido a cada minuto.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => {
      setNow(new Date())
    }, 60_000)
    return () => {
      clearInterval(t)
    }
  }, [])

  useEffect(() => {
    if (openCreate) setCreateOpen(true)
  }, [openCreate])

  const labels = useMemo(
    () => new Map((factories ?? []).map((f) => [f.slug, f.label] as const)),
    [factories],
  )
  const cards = useMemo(
    () => visiblePools(buildPoolCards(pools ?? [], labels, now), ''),
    [pools, labels, now],
  )
  const selected = (pools ?? []).find((p) => p.slug === selectedSlug) ?? null

  // ``/pools`` reopens the last pool opened in this browser, or the first one
  // of the carousel when none was (or the stored one is gone). Decided during
  // render so nothing flashes before the redirect.
  const reopen =
    selectedSlug === null && !openCreate && pools
      ? poolToReopen(
          readLastPool(),
          cards.map((c) => c.slug),
        )
      : null
  useEffect(() => {
    if (reopen)
      void navigate({
        to: '/pools/$slug',
        params: { slug: reopen },
        search: keepChannel,
        replace: true,
      })
  }, [reopen, navigate])
  useEffect(() => {
    if (selected) rememberPool(selected.slug)
  }, [selected])

  // Trocar de pool não enche o histórico: o "voltar" sai da tela.
  const select = (slug: string) => {
    void navigate({ to: '/pools/$slug', params: { slug }, search: keepChannel, replace: true })
  }

  const closeCreate = () => {
    setCreateOpen(false)
    if (openCreate) void navigate({ to: '/pools', search: keepChannel })
  }

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Automação"
        title="Pools"
        description="Acervos de vídeo sem marca, compartilhados entre os canais da sua conta."
        actions={
          <Button
            leadingIcon={<Plus />}
            onClick={() => {
              setCreateOpen(true)
            }}
          >
            Novo pool
          </Button>
        }
      />

      {isPending || reopen ? (
        <p className={styles.muted}>Carregando…</p>
      ) : isError ? (
        <QueryErrorNotice
          error={error}
          label="os pools"
          onRetry={() => {
            void refetch()
          }}
        />
      ) : cards.length === 0 ? (
        <EmptyState
          icon={<Layers />}
          title="Nenhum pool ainda"
          description="Um pool gera vídeos sem marca e vários canais publicam dele, cada vídeo num canal só."
          action={
            <Button
              leadingIcon={<Plus />}
              onClick={() => {
                setCreateOpen(true)
              }}
            >
              Criar o primeiro pool
            </Button>
          }
        />
      ) : (
        <>
          <PoolCarousel
            cards={cards}
            selectedSlug={selected ? selected.slug : null}
            onSelect={select}
            onCreate={() => {
              setCreateOpen(true)
            }}
          />
          {selected ? (
            <PoolDetail
              pool={selected}
              factoryLabel={labels.get(selected.factory) ?? selected.factory}
              now={now}
            />
          ) : selectedSlug ? (
            // Link velho ou pool apagado em outra aba: o quebrado fica visível, sem redirecionar sozinho.
            <EmptyState
              icon={<Layers />}
              title="Pool não encontrado"
              description="Ele pode ter sido apagado."
              action={
                cards[0] ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (cards[0]) select(cards[0].slug)
                    }}
                  >
                    Abrir {cards[0].name}
                  </Button>
                ) : undefined
              }
            />
          ) : null}
        </>
      )}

      <CreatePoolDialog open={createOpen} onClose={closeCreate} />
    </div>
  )
}
